import React, { useState, useEffect } from "react";
import { Modal, ModalBody, ModalHeader, Card, CardBody, Row, Col, Alert, Button } from "reactstrap";
// service
import { getSalesDetails, markOfflinePaid, reissueInvoice } from "../../../utils/services/sales";
// utils
import {
  documentDigits,
  formatAmount,
  formatDateTime,
  formatDocument,
  formatMobile,
  mailtoLink,
  whatsappLink,
} from "./ConsolidatedSales/formatters";
import { buildCustomerItems } from "./ConsolidatedSales/attendeeItems";
import { renderSafeHtml } from "./ConsolidatedSales/safeHtml";
// componentes
import { CopyButton } from "./ConsolidatedSales/CopyValue";
import ModalInvoiceAction from "./ConsolidatedSales/ModalInvoiceAction";

const PAYMENT_TYPES = {
  pix: "Pix",
  credit_card: "Cartão de Crédito",
  boleto: "Boleto",
  free: "Gratuito",
};

const PAYMENT_STATUS = {
  pending: { text: "Pendente", badge: "badge-soft-warning" },
  paid: { text: "Pago", badge: "badge-soft-success" },
  canceled: { text: "Cancelado", badge: "badge-soft-danger" },
  expired: { text: "Expirado", badge: "badge-soft-secondary" },
  refunded: { text: "Reembolsado", badge: "badge-soft-info" },
};

// ações na fatura IUGU (a API confere de novo): nova fatura também para venda cancelada;
// pagamento offline também em venda cancelada (pagou por fora depois do cancelamento)
const INVOICE_PAYMENT_TYPES = ["pix", "boleto", "credit_card"];
const REISSUE_STATUSES = ["pending", "expired", "canceled"];
const OFFLINE_STATUSES = ["pending", "expired", "canceled"];

const hasInvoice = (row) =>
  Boolean(
    row &&
      row.transaction_id &&
      row.processor_id &&
      row.invoice_url &&
      INVOICE_PAYMENT_TYPES.includes(row.payment_type)
  );

export function canReissueInvoice(row) {
  return hasInvoice(row) && REISSUE_STATUSES.includes(row.payment_status);
}

export function canMarkOfflinePaid(row) {
  return hasInvoice(row) && OFFLINE_STATUSES.includes(row.payment_status);
}

const GENERIC_ERROR = "Não foi possível concluir a ação. Tente novamente em breve.";

// Ações sobre a fatura IUGU: textos da confirmação, chamada à API e aviso de sucesso.
const INVOICE_ACTIONS = {
  reissue: {
    title: "Gerar nova fatura",
    description:
      "A fatura atual será cancelada na IUGU e uma nova será gerada com o mesmo prazo de vencimento, contado a partir de hoje.",
    note: "O link da fatura anterior deixa de valer para o comprador.",
    confirmLabel: "Gerar nova fatura",
    icon: "mdi-file-refresh-outline",
    tone: "warning",
    color: "primary",
    request: reissueInvoice,
    succeeded: (res) => Boolean(res?.processor_id),
    success: (res) => {
      const due = formatDay(res.due_date);
      return `Nova fatura gerada${due ? ` com vencimento em ${due}` : ""}.`;
    },
  },
  offline: {
    title: "Marcar como pago",
    description:
      "Use quando o comprador pagou fora da IUGU. A venda passa para Pago e a fatura atual é cancelada na IUGU (se ainda estiver em aberto).",
    note: "Não cadastra o participante no MICE nem envia e-mails.",
    confirmLabel: "Marcar como pago",
    icon: "mdi-cash-check",
    tone: "success",
    color: "success",
    request: markOfflinePaid,
    succeeded: (res) => res?.payment_status === "paid",
    success: (res) => {
      if (res?.invoice_status === "paid") {
        return "A fatura já estava paga na IUGU: a venda foi marcada como paga, sem cancelar a fatura.";
      }
      if (res?.invoice_status === "canceled") {
        return "Venda marcada como paga. A fatura já estava cancelada na IUGU.";
      }
      return "Venda marcada como paga e fatura cancelada na IUGU.";
    },
  },
};

// "2026-10-02" → "02/10/2026"
const formatDay = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))
    ? String(value).split("-").reverse().join("/")
    : "";

// normaliza para texto; nulo/vazio vira ""
const text = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

// Grupos exibidos no modal, com rótulo em português e formatação por campo.
// `keys` aceita nomes alternativos para cobrir a consulta legada da API.
const GROUPS = [
  {
    title: "Pagamento",
    icon: "mdi-credit-card-outline",
    fields: [
      { keys: ["payment_status"], label: "Status", type: "status" },
      {
        keys: ["payment_type"],
        label: "Forma de pagamento",
        format: (v) => PAYMENT_TYPES[v] || text(v),
      },
      { keys: ["amount"], label: "Valor total", format: formatAmount },
      {
        keys: ["installments"],
        label: "Parcelas",
        format: (v) => (Number(v) > 0 ? `${v}x` : ""),
      },
      { keys: ["promo_code"], label: "Cupom" },
      { keys: ["event_code"], label: "ID Evento no MICE", copy: true },
      {
        keys: ["processor_id"],
        label: "IUGU",
        mono: true,
        copy: true,
        // link para a fatura pública na IUGU (secure_url, exposta pela API como invoice_url)
        href: (raw, row) => row.invoice_url || "",
        title: "Abrir fatura na IUGU",
        action: "reissue",
      },
      { keys: ["transaction_id"], label: "ID", mono: true, copy: true },
      { keys: ["created_at"], label: "Criado em", format: formatDateTime },
      { keys: ["updated_at"], label: "Atualizado em", format: formatDateTime },
    ],
  },
  {
    title: "Comprador",
    icon: "mdi-account-cash-outline",
    fields: [
      { keys: ["customer_name"], label: "Nome" },
      {
        keys: ["customer_email", "customeor_email"],
        label: "E-mail",
        copy: true,
        href: mailtoLink,
      },
      {
        keys: ["customer_document"],
        label: "CPF/CNPJ",
        format: formatDocument,
        copy: true,
        copyValue: documentDigits,
      },
      {
        keys: ["customer_mobile"],
        label: "Celular",
        format: formatMobile,
        copy: true,
        href: whatsappLink,
      },
      { keys: ["customer_address"], label: "Endereço" },
      { keys: ["customer_number"], label: "Número" },
      { keys: ["customer_neighborhood"], label: "Bairro" },
      { keys: ["customer_city"], label: "Cidade" },
      { keys: ["customer_state"], label: "UF" },
      { keys: ["customer_zip_code"], label: "CEP" },
    ],
  },
  {
    title: "Participante",
    icon: "mdi-account-outline",
    fields: [
      { keys: ["name", "nome"], label: "Nome" },
      { keys: ["email"], label: "E-mail" },
      { keys: ["cpf"], label: "CPF", format: formatDocument },
      { keys: ["mobile"], label: "Celular", format: formatMobile },
      { keys: ["empresa"], label: "Empresa" },
      { keys: ["cargo"], label: "Cargo" },
      { keys: ["mice_id"], label: "ID no MICE" },
      { keys: ["login_id"], label: "ID do login", mono: true },
    ],
  },
  {
    title: "Produto",
    icon: "mdi-ticket-confirmation-outline",
    fields: [
      { keys: ["description"], label: "Produto" },
      { keys: ["description2"], label: "Descrição" },
      { keys: ["quantity"], label: "Quantidade" },
      { keys: ["price"], label: "Preço (cartão)", format: formatAmount },
      { keys: ["pix_price"], label: "Preço (Pix)", format: formatAmount },
      {
        keys: ["bankslip_price"],
        label: "Preço (boleto)",
        format: formatAmount,
      },
    ],
  },
];

// monta os itens de um grupo a partir da linha; campos vazios ficam de fora
function buildItems(fields, row) {
  return fields
    .map((field) => {
      const key = field.keys.find((k) => text(row[k]) !== "");
      if (!key) return null;
      const value = field.format ? field.format(row[key]) : text(row[key]);
      if (!value) return null;
      // `href` recebe o valor bruto e a linha inteira (ex.: IUGU usa row.invoice_url)
      const link = field.href ? field.href(row[key], row) : "";
      return { ...field, value, raw: row[key], link };
    })
    .filter(Boolean);
}

// Comprador: aos campos das colunas da transação somam-se os campos customizáveis do
// formulário de cobrança (`customer_info`), rotulados por `customer_fields` como no card
// do participante (rótulo pode ter HTML). Sem customer_info, fica só o que já era exibido.
function buildGroupItems(group, row, customerFields) {
  const items = buildItems(group.fields, row);
  if (group.title !== "Comprador") return items;
  const custom = buildCustomerItems(row.customer_info, customerFields).map((item) => ({
    ...item,
    key: `custom:${item.name}`,
    html: true,
  }));
  return items.concat(custom);
}

const statusText = (value) => PAYMENT_STATUS[value]?.text || text(value);

// Partes da frase do histórico, conforme a ação e o status da fatura na IUGU no momento
// (`invoice_status`; entradas antigas não têm e contam como canceladas pela ação):
// - nova fatura: "<usuário> cancelou a fatura X e gerou uma nova fatura em <data>"; já
//   cancelada: "<usuário> gerou uma nova fatura no lugar da fatura X (já cancelada na IUGU) em…"
// - marcar como pago: "<usuário> cancelou a fatura X, alterando de A para B em…"; já cancelada:
//   "<usuário> alterou o status da fatura X (já cancelada na IUGU) de A para B em…"; já paga:
//   "<usuário> alterou o status da fatura X de A para B (já paga na IUGU) em…"
export function invoiceLogWords(item) {
  const status = item.invoice_status;
  if (item.current_status) {
    if (status === "paid") {
      return { verb: "alterou o status da fatura", note: "", change: " de", suffix: " (já paga na IUGU)" };
    }
    if (status === "canceled") {
      return { verb: "alterou o status da fatura", note: " (já cancelada na IUGU)", change: " de", suffix: "" };
    }
    return { verb: "cancelou a fatura", note: "", change: ", alterando de", suffix: "" };
  }
  if (status === "canceled") {
    return { verb: "gerou uma nova fatura no lugar da fatura", note: " (já cancelada na IUGU)", change: null, suffix: "" };
  }
  return { verb: "cancelou a fatura", note: " e gerou uma nova fatura", change: null, suffix: "" };
}

export function InvoiceLogCard({ items }) {
  if (!items || items.length === 0) return null;
  const words = invoiceLogWords;
  return (
    <Card className="shadow-sm border-0 mb-3">
      <CardBody>
        <div className="d-flex align-items-center gap-2 mb-3">
          <span
            className="avatar-title rounded-circle bg-primary bg-soft text-primary"
            style={{ width: 32, height: 32 }}
          >
            <i className="mdi mdi-history font-size-16" aria-hidden="true" />
          </span>
          <h6 className="mb-0">Histórico da fatura</h6>
        </div>
        <ul className="list-unstyled mb-0">
          {items.map((item, index) => (
            <li
              key={`${item.processor_id}-${item.updated_at || index}`}
              className={index > 0 ? "border-top pt-2 mt-2" : ""}
            >
              <span className="fw-semibold">{item.login_name || "Usuário não identificado"}</span>{" "}
              {words(item).verb}{" "}
              <span className="font-monospace font-size-12 text-break">
                {item.invoice_url ? (
                  <a
                    href={item.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir fatura na IUGU"
                  >
                    {item.processor_id}
                  </a>
                ) : (
                  item.processor_id
                )}
              </span>
              {words(item).note}
              {words(item).change && (
                <>
                  {words(item).change}{" "}
                  <span className="fw-semibold">{statusText(item.previous_status)}</span> para{" "}
                  <span className="fw-semibold">{statusText(item.current_status)}</span>
                  {words(item).suffix}
                </>
              )}
              {item.updated_at && <> em {formatDateTime(item.updated_at)}</>}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

// `sections` limita os grupos exibidos (ex.: ["Pagamento", "Comprador"] na listagem,
// onde o detalhe é da venda e não de um participante); sem `sections`, mostra todos.
export default function ModalDetails(props) {
  const { isOpen, toggle, saleId, sale, token, sections, onChanged } = props;
  // linha exibida: vem pronta (sale) ou é buscada pelo id da transação (saleId)
  const [data, setData] = useState(sale || null);
  // campos do formulário de cobrança do evento (rótulos dos extras do comprador)
  const [customerFields, setCustomerFields] = useState([]);
  // trocas de fatura IUGU (transactions.log), mais recentes primeiro
  const [invoiceLog, setInvoiceLog] = useState([]);
  // carregamento
  const [loading, setLoading] = useState(!sale);
  // ação na fatura IUGU em confirmação ("reissue" | "offline"), envio e resultado no topo
  const [invoiceAction, setInvoiceAction] = useState(null);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // busca os detalhes quando só o id da transação foi informado
  function getInfo() {
    getSalesDetails({ jwt: token, idSale: saleId })
      .then((res) => {
        const rows = res?.transactions || [];
        const found = rows.find((e) => e.transaction_id === saleId) || rows[0];
        setData(found || null);
        setCustomerFields(Array.isArray(res?.customer_fields) ? res.customer_fields : []);
        setInvoiceLog(Array.isArray(res?.invoice_log) ? res.invoice_log : []);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!sale) getInfo();
  }, []);

  function openInvoiceAction(action) {
    setFeedback(null);
    setInvoiceAction(action);
  }

  // executa a ação confirmada; no sucesso recarrega o detalhe e avisa a listagem
  function confirmInvoiceAction() {
    const action = INVOICE_ACTIONS[invoiceAction];
    setRunning(true);
    action
      .request({ jwt: token, idSale: data.transaction_id, processorId: data.processor_id })
      .then((res) => {
        if (action.succeeded(res)) {
          setFeedback({ color: "success", text: action.success(res) });
          getInfo();
          if (onChanged) onChanged();
        } else {
          setFeedback({ color: "danger", text: res?.data?.errors?.[0] || GENERIC_ERROR });
        }
      })
      .catch(() => setFeedback({ color: "danger", text: GENERIC_ERROR }))
      .finally(() => {
        setRunning(false);
        setInvoiceAction(null);
      });
  }

  // valor de um item: badge para status, monoespaçado para ids, texto para o resto
  function renderValue(item) {
    if (item.type === "status") {
      const status = PAYMENT_STATUS[item.raw] || {
        text: item.value,
        badge: "badge-soft-secondary",
      };
      return (
        <span className={`badge rounded-pill px-3 py-1 ${status.badge}`}>
          {status.text}
        </span>
      );
    }
    // link (mailto:/WhatsApp/fatura IUGU) quando o campo define `href`; sem link, texto simples
    const value = (
      <span
        className={`fw-semibold text-break ${
          item.mono ? "font-monospace font-size-12" : ""
        }`}
      >
        {item.link ? (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            title={item.title}
          >
            {item.value}
          </a>
        ) : (
          item.value
        )}
      </span>
    );
    if (!item.copy) return value;
    return (
      <span className="d-inline-flex align-items-center gap-2">
        {value}
        <CopyButton
          value={item.copyValue ? item.copyValue(item.raw) : item.raw}
          label={item.label}
        />
      </span>
    );
  }

  const visibleGroups = sections
    ? GROUPS.filter((group) => sections.includes(group.title))
    : GROUPS;
  const groups = data
    ? visibleGroups
        .map((group) => ({ ...group, items: buildGroupItems(group, data, customerFields) }))
        .filter((group) => group.items.length > 0)
    : [];

  // subtítulo só quando o detalhe é de um participante (nome · produto); na visão da
  // venda os dados do comprador já aparecem no card Comprador
  const participantView = !sections || sections.includes("Participante");
  const subtitle =
    data && participantView
      ? [data.name || data.nome, data.description].filter(Boolean).join(" · ")
      : "";

  return (
    <Modal isOpen={isOpen} centered size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        <div>Detalhes da venda</div>
        {subtitle && (
          <div className="text-muted fw-normal font-size-13">{subtitle}</div>
        )}
      </ModalHeader>
      <ModalBody className="bg-light">
        {feedback && (
          <Alert color={feedback.color} toggle={() => setFeedback(null)} className="mb-3">
            {feedback.text}
          </Alert>
        )}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="circle-loading" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i
              className="mdi mdi-file-search-outline d-block mb-2"
              style={{ fontSize: 40 }}
              aria-hidden="true"
            />
            Nenhum detalhe encontrado para esta venda
          </div>
        ) : (
          groups.map((group) => (
            <React.Fragment key={group.title}>
              <Card className="shadow-sm border-0 mb-3">
                <CardBody>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span
                      className="avatar-title rounded-circle bg-primary bg-soft text-primary"
                      style={{ width: 32, height: 32 }}
                    >
                      <i className={`mdi ${group.icon} font-size-16`} aria-hidden="true" />
                    </span>
                    <h6 className="mb-0">{group.title}</h6>
                  </div>
                  <Row className="g-3">
                    {group.items.map((item) => (
                      <Col xs={12} md={6} key={item.key || item.label}>
                        <div className="text-muted font-size-12">
                          {item.html ? renderSafeHtml(item.label) : item.label}
                        </div>
                        {renderValue(item)}
                        {item.action === "reissue" && saleId && (canReissueInvoice(data) || canMarkOfflinePaid(data)) && (
                          <div className="mt-2 d-flex flex-wrap gap-2">
                            {canReissueInvoice(data) && (
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                                onClick={() => openInvoiceAction("reissue")}
                              >
                                <i className="mdi mdi-file-refresh-outline" aria-hidden="true" />
                                Gerar nova fatura
                              </Button>
                            )}
                            {canMarkOfflinePaid(data) && (
                              <Button
                                color="success"
                                outline
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                                onClick={() => openInvoiceAction("offline")}
                              >
                                <i className="mdi mdi-cash-check" aria-hidden="true" />
                                Marcar como pago
                              </Button>
                            )}
                          </div>
                        )}
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
              {group.title === "Comprador" && <InvoiceLogCard items={invoiceLog} />}
            </React.Fragment>
          ))
        )}
      </ModalBody>
      {invoiceAction && (
        <ModalInvoiceAction
          {...INVOICE_ACTIONS[invoiceAction]}
          isOpen
          toggle={() => setInvoiceAction(null)}
          processorId={data?.processor_id}
          loading={running}
          onConfirm={confirmInvoiceAction}
        />
      )}
    </Modal>
  );
}
